import { InfoCircledIcon, TrashIcon, UploadIcon } from '@radix-ui/react-icons';
import { Box, Button, Flex, Text } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { CommonCallout } from 'components/common/callouts';
import { CommonDialog } from 'components/common/dialogs';
import { ErrorBoundary } from 'components/common/error-boundary';
import { filterFiles } from 'components/common/file-uploader';
import { CommonFormGrid } from 'components/common/form/grid';
import { CommonInputLabel } from 'components/common/form/label';
import { CommonPhoneInput } from 'components/common/form/phone';
import { CommonSearchInput } from 'components/common/form/search';
import { CommonSelectInput } from 'components/common/form/select';
import { CommonTextInput } from 'components/common/form/text';
import { CommonHitterAvatar } from 'components/common/hitters/avatar';
import { HittersContext } from 'contexts/hitters.context';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import { t } from 'i18next';
import { IFullDialog } from 'interfaces/i-dialogs';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { FT_TO_INCHES } from 'lib_ts/classes/math.utilities';
import { ImageMime } from 'lib_ts/enums/files.enums';
import {
  HITTER_SAFETY_BUFFERS,
  HitterDataSource,
  HitterSafety,
  HitterSide,
} from 'lib_ts/enums/hitters.enums';
import {
  HitterTag,
  PLAYER_LEVELS,
  PlayerLevel,
} from 'lib_ts/enums/pitches.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IOption } from 'lib_ts/interfaces/common/i-option';
import { IHitter } from 'lib_ts/interfaces/i-hitter';
import { clone, first } from 'lodash';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { PlayersService } from 'services/players.service';

const COMPONENT_NAME = 'HitterEditorDialog';

const MIN_HEIGHT_FT = 4;
const DEFAULT_HEIGHT_FT = 6;
const MAX_HEIGHT_FT = 7;

interface IProps {
  /** undefined implies creation of a new hitter (and next/prev buttons will not be shown) */
  hitter_id: string | undefined;

  /** upon successful creation, the new hitter will be passed back to parent */
  onCreate?: (value: IHitter) => void;

  /** upon successful update, the new hitter will be passed back to parent */
  onUpdate?: (value: IHitter) => void;

  onClose: () => void;
}

const DEFAULT_HITTER: Partial<IHitter> = {
  height_ft: DEFAULT_HEIGHT_FT,
};

const FEET_OPTIONS: IOption[] = ArrayHelper.getIntegerOptions(
  MIN_HEIGHT_FT,
  MAX_HEIGHT_FT
).map((o) => ({
  ...o,
  label: `${o.label}'`,
}));

const INCH_OPTIONS: IOption[] = ArrayHelper.getIntegerOptions(0, 11).map(
  (o) => ({
    ...o,
    label: `${o.label}"`,
  })
);

const TAGS_OPTIONS = Object.values(HitterTag).map((m) => {
  const o: IOption = {
    label: m,
    value: m,
  };

  return o;
});

export const EditHitterDialog = (props: IProps) => {
  const { hitters, loading, create, update } = useContext(HittersContext);

  // todo: should this string be an enum or something?
  const playerAppEnabled = useFeatureFlag('player-app');

  const [hitter, setHitter] = useState(clone(DEFAULT_HITTER));

  // only set when user uploads a custom avatar
  const [customAvatarFile, setCustomAvatarFile] = useState<File>();

  const customAvatarSrc = useMemo(() => {
    if (!customAvatarFile) {
      return;
    }

    return URL.createObjectURL(customAvatarFile);
  }, [customAvatarFile]);

  useEffect(() => {
    const hitter =
      hitters.find((h) => h._id === props.hitter_id) ?? clone(DEFAULT_HITTER);

    setHitter(hitter);
  }, [props.hitter_id, hitters]);

  // '' when blank, null when invalid, E.164 when valid
  const [phoneInput, setPhoneInput] = useState<string | null>('');
  // the linked phone from the server, '' when none
  const [loadedPhone, setLoadedPhone] = useState('');
  const [loadingPlayer, setLoadingPlayer] = useState(!!props.hitter_id);

  // outline phone input in red when invalid
  const [showPhoneError, setShowPhoneError] = useState(false);

  useEffect(() => {
    // new hitter, no need to fetch player
    if (!props.hitter_id) {
      setPhoneInput('');
      setLoadedPhone('');
      setLoadingPlayer(false);
      return;
    }

    let cancelled = false;
    // clear any previous hitter's number before the input remounts for this fetch
    setPhoneInput('');
    setLoadedPhone('');
    setLoadingPlayer(true);

    const fetchPlayer = async () => {
      if (!props.hitter_id) {
        return;
      }

      const player = await PlayersService.getInstance().getHitterPlayer(
        props.hitter_id
      );

      // a cancelled request must not flip loading off; that remounts the phone
      // input with an empty defaultValue before the active request stores the number
      if (cancelled) {
        return;
      }

      setPhoneInput(player?.phone ?? '');
      setLoadedPhone(player?.phone ?? '');
      setLoadingPlayer(false);
    };

    fetchPlayer();

    return () => {
      cancelled = true;
    };
  }, [props.hitter_id]);

  const trimmedPhone = (phoneInput ?? '').trim();

  const safeHeight = hitter.height_ft ?? DEFAULT_HEIGHT_FT;

  const statsApiReadOnly = hitter.source === HitterDataSource.StatsApi;

  const formRef = useRef<HTMLFormElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleUploadHeadshot = useCallback(() => {
    if (!formRef.current) {
      return;
    }

    // so that uploading the same filename repeatedly will work
    formRef.current.reset();

    if (!inputRef.current) {
      return;
    }

    // open the OS's file selector dialog
    inputRef.current.click();
  }, [inputRef.current]);

  const onSave = useCallback(async () => {
    const payload = hitter;
    payload.name = (payload.name ?? '').trim();

    // undefined leaves the player link alone, so a hidden or unchanged input never unlinks a player
    let phone: string | null | undefined = undefined;
    if (playerAppEnabled && trimmedPhone !== loadedPhone) {
      // a cleared input unlinks the player
      phone = trimmedPhone || null;
    }

    if (!payload.name) {
      NotifyHelper.warning({
        message_md: t('common.x-is-required-check-inputs', {
          x: t('common.name'),
        }),
      });
      return;
    }

    if (!payload.side) {
      NotifyHelper.warning({
        message_md: t('common.x-is-required-check-inputs', {
          x: t('common.side'),
        }),
      });
      return;
    }

    if (!payload.level) {
      NotifyHelper.warning({
        message_md: t('common.x-is-required-check-inputs', {
          x: t('common.level'),
        }),
      });
      return;
    }

    if (playerAppEnabled && phoneInput === null) {
      setShowPhoneError(true);
      NotifyHelper.warning({
        message_md: t('hitters.please-enter-a-valid-phone-number', {
          x: t('hitters.phone-number'),
        }),
      });
      return;
    }

    if (!payload._id) {
      const success = await create(
        { ...payload, phone },
        props.onCreate,
        customAvatarFile
      );

      if (success) {
        props.onClose();
      }
      return;
    }

    const success = await update({
      payload: {
        ...payload,
        _id: payload._id,
        phone,
      },
      customAvatarFile: customAvatarFile,
      onUpdate: props.onUpdate,
    });

    if (success) {
      props.onClose();
    }
  }, [
    hitter,
    create,
    update,
    props.onClose,
    props.onCreate,
    props.onUpdate,
    trimmedPhone,
    loadedPhone,
    customAvatarFile,
    loadingPlayer,
    phoneInput,
    playerAppEnabled,
  ]);

  const fromStatsApi = hitter.source === HitterDataSource.StatsApi;

  const content = (
    <CommonFormGrid columns={2}>
      <Flex gridColumn="span 2" direction="column" gap="2">
        <CommonInputLabel label="hitters.headshot" />

        <Flex gap="2" align="center">
          <CommonHitterAvatar
            size="7"
            hitter={hitter}
            customFallback="+"
            clickDisabled={statsApiReadOnly}
            onClick={handleUploadHeadshot}
            src={customAvatarSrc}
          />

          {!statsApiReadOnly && (
            <Flex direction="column" gap="2">
              <Button
                size="1"
                variant={RADIX.BUTTON.VARIANT.SECONDARY}
                disabled={statsApiReadOnly}
                loading={loading}
                onClick={handleUploadHeadshot}
              >
                <UploadIcon /> {t('common.upload')}
              </Button>
              <Button
                size="1"
                variant={RADIX.BUTTON.VARIANT.SECONDARY}
                color={RADIX.COLOR.DANGER}
                className={
                  customAvatarFile || hitter.avatar_path ? '' : 'hidden'
                }
                loading={loading}
                onClick={async () => {
                  setCustomAvatarFile(undefined);

                  if (!hitter._id) {
                    return;
                  }

                  setHitter((prev) => ({
                    ...prev,
                    avatar_path: null,
                    avatar_mime: null,
                  }));
                }}
              >
                <TrashIcon /> {t('common.delete')}
              </Button>
            </Flex>
          )}

          <form ref={formRef} hidden>
            <input
              type="file"
              ref={inputRef}
              onChange={(e) => {
                const { files } = e.target;
                const file = first(files);

                if (!file || !files) {
                  // one file is required
                  return;
                }

                const validFiles = filterFiles({
                  allFiles: [...files],
                  fileTypes: Object.values(ImageMime),
                  notifyMode: 'each',
                  maxMB: 2,
                });

                if (validFiles.length === 0) {
                  return;
                }

                console.log('Uploading hitter avatar file:', file);
                setCustomAvatarFile(file);
              }}
              accept={Object.values(ImageMime).join(', ')}
            />
          </form>
        </Flex>

        {!statsApiReadOnly && (
          <Text color={RADIX.COLOR.HINT}>
            {t('common.recommended')}: PNG, 256x256px
          </Text>
        )}
      </Flex>

      <Box gridColumn="span 2">
        <CommonTextInput
          id="hitter-name"
          name="hitter.name"
          label="common.name"
          value={hitter.name}
          placeholder={t('common.type-a-unique-value') as string}
          disabled={loading || statsApiReadOnly}
          onChange={(v) =>
            setHitter((prev) => ({
              ...prev,
              name: v,
            }))
          }
        />
      </Box>

      <Box gridColumn="span 2">
        <CommonSelectInput
          id="hitter-level"
          name="hitter.level"
          label="common.level"
          options={PLAYER_LEVELS}
          value={hitter.level}
          onChange={(v) => {
            const nextLevel = v as PlayerLevel;

            setHitter((prev) => ({
              ...prev,
              level: nextLevel,
              tags: nextLevel === PlayerLevel.Collegiate ? prev.tags : [],
            }));
          }}
          disabled={loading || statsApiReadOnly}
          optional
          skipSort
        />
      </Box>

      {hitter.level === PlayerLevel.Collegiate && (
        <Box gridColumn="span 2">
          <CommonSearchInput
            id="hitter-tags"
            label="common.tags"
            options={TAGS_OPTIONS}
            values={hitter.tags ?? []}
            onChange={(values) => {
              setHitter((prev) => ({
                ...prev,
                tags: values as HitterTag[],
              }));
            }}
            disabled={loading}
            skipSort
            multiple
          />
        </Box>
      )}

      <CommonSelectInput
        id="hitter-height-ft"
        label="common.height"
        options={FEET_OPTIONS}
        value={Math.floor(safeHeight).toString()}
        disabled={loading || statsApiReadOnly}
        onNumericChange={(v) =>
          setHitter((prev) => ({
            ...prev,
            height_ft: v + (safeHeight % 1),
          }))
        }
        skipSort
      />

      <CommonSelectInput
        id="hitter-height-in"
        label=" "
        options={INCH_OPTIONS}
        value={Math.round((safeHeight % 1) * FT_TO_INCHES).toString()}
        disabled={loading || statsApiReadOnly}
        onNumericChange={(v) => {
          const inches = v / FT_TO_INCHES;

          setHitter((prev) => ({
            ...prev,
            height_ft: hitter.height_ft
              ? Math.floor(hitter.height_ft) + inches
              : inches,
          }));
        }}
        skipSort
      />

      <CommonSelectInput
        id="hitter-side"
        name="hitter.side"
        label="common.side"
        options={Object.values(HitterSide).map((s) => ({
          label: t(`hitters.${s}`.toLowerCase()),
          value: s,
        }))}
        value={hitter.side}
        onChange={(v) =>
          setHitter((prev) => ({
            ...prev,
            side: v as HitterSide,
          }))
        }
        disabled={loading || statsApiReadOnly}
        optional
      />

      <CommonSelectInput
        id="hitter-safety"
        name="hitter.safety_buffer"
        label="hitters.safety-buffer"
        options={HITTER_SAFETY_BUFFERS}
        value={hitter.safety_buffer}
        onChange={(v) =>
          setHitter((prev) => ({
            ...prev,
            safety_buffer: v as HitterSafety,
          }))
        }
        disabled={loading}
        optional
        skipSort
      />

      {playerAppEnabled && (
        <Box gridColumn="span 2">
          <CommonPhoneInput
            // remount once the fetch settles so useState picks up defaultValue
            key={
              loadingPlayer
                ? `${props.hitter_id ?? 'new'}:loading`
                : (props.hitter_id ?? 'new')
            }
            id="hitter-phone-number"
            label="hitters.phone-number"
            hint_md={t('hitters.player-phone-hint').toString()}
            defaultValue={loadingPlayer ? undefined : (phoneInput ?? undefined)}
            disabled={loading || loadingPlayer}
            showError={showPhoneError}
            onChange={setPhoneInput}
          />
        </Box>
      )}

      <Box gridColumn={fromStatsApi ? undefined : 'span 2'}>
        <CommonTextInput
          id="hitter-customID"
          name="hitter.customID"
          label="hitters.custom-id"
          value={hitter.customID}
          placeholder={t('common.type-a-unique-value') as string}
          disabled={loading}
          onChange={(v) =>
            setHitter((prev) => ({
              ...prev,
              customID: v,
            }))
          }
        />
      </Box>

      {fromStatsApi && (
        <Box>
          <CommonTextInput
            id="hitter-playerPk"
            name="hitter.playerPk"
            label="MLB ID"
            value={hitter.statsApiData?.playerPk.toString()}
            disabled // read-only
          />
        </Box>
      )}

      {fromStatsApi && (
        <Box gridColumn="span 2">
          <CommonCallout
            flexAlign="start"
            icon={<InfoCircledIcon />}
            color={RADIX.COLOR.ACCENT}
            content="This hitter was imported through Stats API which makes the personal information uneditable."
          />
        </Box>
      )}
    </CommonFormGrid>
  );

  const mergeProps: IFullDialog = {
    identifier: COMPONENT_NAME,
    width: RADIX.DIALOG.WIDTH.SM,
    title: t(props.hitter_id ? 'common.edit-x' : 'common.create-x', {
      x: t('hitters.hitter'),
    }),
    content: content,
    loading: loading,
    onClose: props.onClose,
    buttons: [
      {
        label: 'common.save',
        variant: 'solid',
        color: RADIX.COLOR.ACCENT,
        loading: loading || loadingPlayer,
        onClick: onSave,
      },
    ],
  };

  return (
    <ErrorBoundary identifier={COMPONENT_NAME}>
      <CommonDialog {...mergeProps} />
    </ErrorBoundary>
  );
};
